import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import ActionModal from '../ActionModal';
import FavoriteHeart from '../../svg/icons/favorite_heart.svg';
import Icon from '../Icon';
import Input from '../Input';
import ListCell from '../ListCell';
import Plus from '../../svg/icons/add.svg';
import TextArea from '../TextArea';
import { getTranslation } from '../../providers/TranslationProvider';
import {
  MODAL_MODES,
  copyListBySlugThunk,
  createListThunk,
  deleteListThunk,
  editListThunk,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  setErrorModalOpenState,
} from '../../reducers/favoriteSlice';

import './MultiStepListModal.scss';
import getNumOfVenuesContent from '../../utils/getNumOfVenuesContent';
import getPageContext from '../../utils/getPageContext';
import checkIfWidget from '../../utils/checkIfWidget';
import { addVenueToListsThunk } from '../../reducers/favoriteVenuesSlice';
import ListCellSkeleton from '../ListCell/ListCellSkeleton';

const MultiStepListModal = (props) => {
  const {
    Analytics,
    description: currentListDescription,
    dispatch,
    error,
    listId,
    lists,
    isLoading,
    isMultiStepModalOpen,
    multiStepModalVenueId,
    multiStepModalVenueContext,
    multiStepModalMode,
    name: currentListName,
    newlyCreatedList,
    urlSlug,
  } = props;

  const [checkedStates, setCheckedStates] = useState({});
  const [isOnlyHitlistSelected, setIsOnlyHitlistSelected] = useState(false);

  const initialCheckedStates = useMemo(() => {
    let hitlistIndex;
    setIsOnlyHitlistSelected(false);

    const checked = lists.reduce((acc, { isVenueOnList, isLegacyHitlist }, index) => {
      acc[index] = isVenueOnList || false;

      if (isLegacyHitlist) hitlistIndex = index;

      return acc;
    }, {});

    if (lists.length <= 3
      && hitlistIndex > -1
      && !Object.values(checked).includes(true)
      && !newlyCreatedList) {
      checked[hitlistIndex] = true;
      setIsOnlyHitlistSelected(true);
    }

    return checked;
  }, [lists]);

  const hasCheckedStateUpdates = !isEqual(initialCheckedStates, checkedStates);
  const hitlistIndex = lists.findIndex(({ isLegacyHitlist }) => isLegacyHitlist);
  const checkedStateDifferences = Object.entries(checkedStates).filter(
    ([field, checkedStatesValue]) => initialCheckedStates[field] !== checkedStatesValue
  );
  const isOnlySyntheticHitlistUndo = isOnlyHitlistSelected
    && checkedStateDifferences.length === 1
    && Number(checkedStateDifferences[0][0]) === hitlistIndex
    && checkedStateDifferences[0][1] === false;
  const hasRealCheckedStateUpdates = hasCheckedStateUpdates && !isOnlySyntheticHitlistUndo;
  const shouldAllowAutoSelectedHitlistSubmit = isOnlyHitlistSelected
    && checkedStateDifferences.length === 0;
  const getEventContext = () => {
    if (multiStepModalVenueContext?.context) {
      return multiStepModalVenueContext.context;
    }

    const pagePath = checkIfWidget()
      ? window.location.hash.replaceAll('#', '')?.split('?')?.[0]
      : window.location.pathname;

    return getPageContext(pagePath);
  };

  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');

  const maxListNameLength = 30;
  const maxListName = listName.length > maxListNameLength;
  const emptyListName = listName.trim().length === 0;
  const maxListDescriptionLength = 350;
  const maxListDescription = listDescription?.length > maxListDescriptionLength;
  const hasSubmitError = emptyListName || maxListName || maxListDescription;
  const isButtonDisabled = hasSubmitError && !isLoading;

  function getModalStyles(modalMode) {
    switch (modalMode) {
      case MODAL_MODES.COPY_LIST:
        return {
          content: {
            maxHeight: '500px',
          },
        };

      case MODAL_MODES.CREATE_LIST:
        return {
          content: {
            maxHeight: '500px',
          },
        };

      case MODAL_MODES.DELETE_LIST:
        return {
          content: {
            maxHeight: '300px',
          },
        };

      case MODAL_MODES.EDIT_LIST:
        return {
          content: {
            maxHeight: '500px',
          },
        };

      case MODAL_MODES.HIT_LIST:
        return {
          content: {
            maxHeight: '300px',
          },
        };

      // MODAL_MODES.ADD_TO_LIST is covered by the default in ActionModal
      default: return {};
    }
  }

  useEffect(() => {
    if (lists.length > 0 && !isEqual(initialCheckedStates, checkedStates)) {
      setCheckedStates(initialCheckedStates);
    }
  }, [lists, initialCheckedStates]);

  useEffect(() => {
    if (newlyCreatedList) {
      const autoSelectListItemIndex = lists.findIndex((list) => list.listId === newlyCreatedList.listId);
      if (autoSelectListItemIndex > -1) {
        setCheckedStates({ ...initialCheckedStates, [autoSelectListItemIndex]: true });
      }
    }
  }, [newlyCreatedList, initialCheckedStates]);

  const onListNameChange = ({ listName: listNameValue }) => {
    setListName(listNameValue);
  };

  const onListDescriptionChange = ({ listDescription: listDescriptionValue }) => {
    setListDescription(listDescriptionValue);
  };

  const getIconComponent = (listInfo) => {
    const venueImg = listInfo?.responsiveImages?.urls[
      listInfo?.responsiveImages.file_names[0]
    ]?.['1:1']?.['200'];

    return venueImg ? (
      <div
        style={{
          backgroundImage: `url(${venueImg})`,
          width: '100%',
          height: '100%',
          backgroundSize: 'cover',
          borderRadius: '10px',
        }}
      />
    ) : (
      <Icon height="30px" width="30px" name="heart" component={FavoriteHeart} />
    );
  };

  const listItems = lists.map((listInfo) => {
    const { count, name } = listInfo;

    return {
      listId,
      titleText: name,
      subTitleText: getNumOfVenuesContent(count),
      iconComponent: getIconComponent(listInfo),
    };
  });

  const onCheckmarkClickHandler = (index, checked) => {
    setCheckedStates({ ...checkedStates, [index]: checked });
  };

  const onCreateListClickHandler = () => {
    dispatch(setMultiStepModalMode(MODAL_MODES.CREATE_LIST));
  };

  const getModalViewFromMode = (mode) => {
    switch (mode) {
      case MODAL_MODES.ADD_TO_LIST: {
        return (
          <div className="MultiStepListModal__list-cells">
            <ListCell
              key="a"
              titleText={getTranslation({
                translationKey: 'lists.create_list',
                fallback: 'Create List',
              })}
              iconComponent={
                <Icon height="30px" width="30px" name="plus" component={Plus} />
              }
              onClick={onCreateListClickHandler}
            />
            {isLoading || listItems.length <= 0 ? (
              <>
                <ListCellSkeleton />
                <ListCellSkeleton />
                <ListCellSkeleton />
                <ListCellSkeleton />
                <ListCellSkeleton />
              </>
            ) : (
              listItems.map((cell, index) => (
                <ListCell
                  key={cell.listId}
                  titleText={cell.titleText}
                  subTitleText={cell.subTitleText}
                  checkmarkState={{
                    checked: checkedStates[index] || false,
                  }}
                  onClick={() => onCheckmarkClickHandler(index, !checkedStates[index])}
                  iconComponent={cell.iconComponent}
                  datadogActionName="Select List"
                />
              ))
            )}
          </div>
        );
      }
      case MODAL_MODES.COPY_LIST: {
        return (
          <div className="MultiStepListModal__create-copy-edit">
            <label
              className="MultiStepListModal__action-modal-list-name"
              htmlFor="listName"
            >
              {getTranslation({
                translationKey: 'lists.name',
                fallback: 'Name',
              })}
            </label>
            <Input
              errors={maxListName ? ['listName'] : ''}
              name="listName"
              onChange={onListNameChange}
              placeholder={getTranslation({
                translationKey: 'lists.date_night_spots',
                fallback: 'Date Night Spots...',
              })}
              value={getTranslation({
                translationKey: 'lists.copy_of_shared_list',
                translationValues: { listName: currentListName },
                fallback: `Copy of ${currentListName}`,
              })}
            />
            <label
              className={cx('MultiStepListModal__action-modal', {
                'MultiStepListModal__action-modal__error': maxListName,
              })}
              htmlFor="listName"
            >
              {getTranslation({
                translationKey: 'lists.max_character_limit',
                fallback: `${maxListNameLength} character limit`,
              })}
            </label>
            <label
              className="MultiStepListModal__action-modal-list-description"
              htmlFor="listDescription"
            >
              {getTranslation({
                translationKey: 'lists.description',
                fallback: 'Description',
              })}
            </label>
            <TextArea
              className="MultiStepListModal__action-modal-list-description-text"
              name="listDescription"
              errors={maxListDescription ? ['listDescription'] : ''}
              onChange={onListDescriptionChange}
              placeholder={getTranslation({
                translationKey: 'lists.optional',
                fallback: 'Optional...',
              })}
              value={currentListDescription}
            />
            <label
              className={cx('MultiStepListModal__action-modal description-max-length', {
                'MultiStepListModal__action-modal__error': maxListDescription,
              })}
              htmlFor="listDescription"
            >
              {getTranslation({
                translationKey: 'lists.max_character_limit',
                fallback: `${maxListDescriptionLength} character limit`,
              })}
            </label>
          </div>
        );
      }
      case MODAL_MODES.CREATE_LIST: {
        return (
          <div className="MultiStepListModal__create-copy-edit">
            <label
              className="MultiStepListModal__action-modal-list-name"
              htmlFor="listName"
            >
              {getTranslation({
                translationKey: 'lists.name',
                fallback: 'Name',
              })}
            </label>
            <Input
              name="listName"
              errors={maxListName ? ['listName'] : ''}
              onChange={onListNameChange}
              placeholder={getTranslation({
                translationKey: 'lists.date_night_spots',
                fallback: 'Date Night Spots...',
              })}
            />
            <label
              className={cx('MultiStepListModal__action-modal', {
                'MultiStepListModal__action-modal__error': maxListName,
              })}
              htmlFor="listName"
              required
            >
              {getTranslation({
                translationKey: 'lists.max_character_limit',
                fallback: `${maxListNameLength} character limit`,
              })}
            </label>
            <label
              className="MultiStepListModal__action-modal-list-description"
              htmlFor="listDescription"
            >
              {getTranslation({
                translationKey: 'lists.description',
                fallback: 'Description',
              })}
            </label>
            <TextArea
              className="MultiStepListModal__action-modal-list-description-text"
              name="listDescription"
              errors={maxListDescription ? ['listDescription'] : ''}
              onChange={onListDescriptionChange}
              placeholder={getTranslation({
                translationKey: 'lists.optional',
                fallback: 'Optional...',
              })}
            />
            <label
              className={cx('MultiStepListModal__action-modal description-max-length', {
                'MultiStepListModal__action-modal__error': maxListDescription,
              })}
              htmlFor="listDescription"
            >
              {getTranslation({
                translationKey: 'lists.max_character_limit',
                fallback: `${maxListDescriptionLength} character limit`,
              })}
            </label>
          </div>
        );
      }
      case MODAL_MODES.EDIT_LIST: {
        return (
          <div className="MultiStepListModal__create-copy-edit">
            <label
              className="MultiStepListModal__action-modal-list-name"
              htmlFor="listName"
            >
              {getTranslation({
                translationKey: 'lists.name',
                fallback: 'Name',
              })}
            </label>
            <Input
              errors={maxListName ? ['listName'] : ''}
              name="listName"
              onChange={onListNameChange}
              value={currentListName}
            />
            <label
              className={cx('MultiStepListModal__action-modal', {
                'MultiStepListModal__action-modal__error': maxListName,
              })}
              htmlFor="listName"
              required
            >
              {getTranslation({
                translationKey: 'lists.max_character_limit',
                fallback: `${maxListNameLength} character limit`,
              })}
            </label>
            <label
              className="MultiStepListModal__action-modal-list-description"
              htmlFor="listDescription"
            >
              {getTranslation({
                translationKey: 'lists.description',
                fallback: 'Description',
              })}
            </label>
            <TextArea
              className="MultiStepListModal__action-modal-list-description-text"
              name="listDescription"
              errors={maxListDescription ? ['listDescription'] : ''}
              onChange={onListDescriptionChange}
              placeholder={getTranslation({
                translationKey: 'lists.optional',
                fallback: 'Optional...',
              })}
              value={currentListDescription}
            />
            <label
              className={cx('MultiStepListModal__action-modal description-max-length', {
                'MultiStepListModal__action-modal__error': maxListDescription,
              })}
              htmlFor="listDescription"
            >
              {getTranslation({
                translationKey: 'lists.max_character_limit',
                fallback: `${maxListDescriptionLength} character limit`,
              })}
            </label>
          </div>
        );
      }
      case MODAL_MODES.DELETE_LIST: {
        return (
          <div className="MultiStepListModal__delete-hitlist">
            <div className="MultiStepListModal__delete-list-modal">
              <h1 className="MultiStepListModal__delete-list-modal__heading">
                {getTranslation({
                  translationKey: 'lists.delete_heading',
                  fallback: 'Sure you want to delete this List?',
                })}
              </h1>
              <div className="MultiStepListModal__delete-list-modal__text">
                {getTranslation({
                  translationKey: 'lists.delete_text',
                  fallback: 'This action can\'t be undone and you will lose this list forever.',
                })}
              </div>
            </div>
          </div>
        );
      }
      case MODAL_MODES.HIT_LIST: {
        return (
          <div className="MultiStepListModal__delete-hitlist">
            <div className="MultiStepListModal__hitlist-modal">
              <h1 className="MultiStepListModal__hitlist-modal__heading">
                {getTranslation({
                  translationKey: 'lists.hitlist_heading',
                  fallback: 'My Hit List cannot be edited or deleted for now',
                })}
              </h1>
              <div className="MultiStepListModal__hitlist-modal__text">
                {getTranslation({
                  translationKey: 'lists.hitlist_text',
                  fallback: 'For now, you can create a new custom list. We\'re working on updates to allow you to edit My Hit List soon!',
                })}
              </div>
            </div>
          </div>
        );
      }
      default: {
        return null;
      }
    }
  };

  const onAddToListButtonPress = useCallback(() => {
    const checkedIndexes = Object.entries(checkedStates).reduce(
      (acc, [index, val]) => (val ? acc.concat(Number(index)) : acc),
      []
    );
    const listIdsToAddToVenue = lists.reduce((acc, val, index) => (checkedIndexes.includes(index) ? acc.concat(val.listId) : acc), []);
    const differences = Object.entries(checkedStates).filter(
      ([field, checkedStatesValue]) => initialCheckedStates[field] !== checkedStatesValue
    );
    const hasSyntheticHitlistUndo = isOnlyHitlistSelected
      && hitlistIndex > -1
      && differences.length === 1
      && Number(differences[0][0]) === hitlistIndex
      && differences[0][1] === false;
    const adjustedDifferences = differences.filter(
      ([field, checkedStatesValue]) => !hasSyntheticHitlistUndo
        || Number(field) !== hitlistIndex
        || checkedStatesValue !== false
    );
    const hasAddedList = adjustedDifferences.some(([, checkedStatesValue]) => checkedStatesValue === true);
    const hasRemovedList = adjustedDifferences.some(([, checkedStatesValue]) => checkedStatesValue === false);
    const shouldTrackAutoSelectedHitListAdd = isOnlyHitlistSelected && differences.length === 0;
    const context = getEventContext();

    if (hasAddedList || shouldTrackAutoSelectedHitListAdd) {
      Analytics.event('add to list', {
        venue: multiStepModalVenueContext.name,
        city: multiStepModalVenueContext.city,
        context: context || undefined,
      });
    }

    if (hasRemovedList) {
      Analytics.event('remove from list', {
        venue: multiStepModalVenueContext.name,
        city: multiStepModalVenueContext.city,
        context: context || undefined,
      });
    }

    dispatch(
      addVenueToListsThunk({
        venueId: multiStepModalVenueId,
        list_ids: listIdsToAddToVenue,
      })
    );
    setCheckedStates({});
  }, [lists, checkedStates, initialCheckedStates, isOnlyHitlistSelected, hitlistIndex, multiStepModalVenueId, multiStepModalVenueContext]);

  const onCopyListButtonPress = (copyListDetails) => {
    Analytics.event('copy list');
    dispatch(copyListBySlugThunk(copyListDetails));
  };

  const onCreateListButtonPress = (createListDetails) => {
    Analytics.event('create list');
    dispatch(createListThunk(createListDetails));
  };

  const onDeleteListButtonPress = (deleteListDetails) => {
    Analytics.event('delete list');
    dispatch(deleteListThunk(deleteListDetails));
  };

  const onEditListButtonPress = (editListDetails) => {
    Analytics.event('edit list');
    dispatch(editListThunk(editListDetails));
  };

  const closeModal = () => {
    dispatch(setMultiStepModalOpenState(false));
  };

  const closeModalAndSave = () => {
    dispatch(setMultiStepModalOpenState(false));
    onAddToListButtonPress();
  };

  const onErrorModalClose = () => {
    dispatch(setErrorModalOpenState(false));
  };

  const getResolvedActionModalProps = (mode) => {
    switch (mode) {
      case MODAL_MODES.ADD_TO_LIST: {
        return {
          modalNavHeaderProps: {
            label: getTranslation({
              translationKey: 'lists.add_to_list',
              fallback: 'Add to List',
            }),
            onCloseIconPress: closeModalAndSave,
          },
          modalFooterProps: {
            hasSecondaryCTA: false,
            primaryCTAText: getTranslation({
              translationKey: 'lists.done',
              fallback: 'Done',
            }),
            primaryCTAVariant: 'primary',
            primaryCTAAdditionalProps: {
              processing: isLoading,
              onClick: onAddToListButtonPress,
              disabled: isLoading || (!hasRealCheckedStateUpdates && !shouldAllowAutoSelectedHitlistSubmit),
            },
          },
          onRequestClose: closeModalAndSave,
        };
      }
      case MODAL_MODES.COPY_LIST: {
        return {
          modalNavHeaderProps: {
            label: getTranslation({
              translationKey: 'lists.copy_to_my_list',
              fallback: 'Copy to My Lists',
            }),
            onBackIconPress: multiStepModalVenueId
              ? () => dispatch(setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST))
              : undefined,
            onCloseIconPress: closeModal,
          },
          modalFooterProps: {
            hasSecondaryCTA: false,
            primaryCTAText: getTranslation({
              translationKey: 'lists.save',
              fallback: 'Save',
            }),
            primaryCTAVariant: 'primary',
            primaryCTAAdditionalProps: {
              disabled: isButtonDisabled,
              processing: isLoading,
              onClick: () => onCopyListButtonPress({
                description: listDescription,
                name: listName,
                slug: urlSlug,
              }),
            },
          },
          onRequestClose: closeModal,
        };
      }
      case MODAL_MODES.CREATE_LIST: {
        return {
          modalNavHeaderProps: {
            label: getTranslation({
              translationKey: 'lists.create_list',
              fallback: 'Create List',
            }),
            onBackIconPress: multiStepModalVenueId
              ? () => dispatch(setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST))
              : undefined,
            onCloseIconPress: closeModal,
          },
          modalFooterProps: {
            hasSecondaryCTA: false,
            primaryCTAText: getTranslation({
              translationKey: 'lists.create_list',
              fallback: 'Create List',
            }),
            primaryCTAVariant: 'primary',
            primaryCTAAdditionalProps: {
              disabled: isButtonDisabled,
              processing: isLoading,
              onClick: () => onCreateListButtonPress({
                name: listName,
                description: listDescription,
              }),
            },
          },
          onRequestClose: closeModal,
        };
      }
      case MODAL_MODES.EDIT_LIST: {
        return {
          modalNavHeaderProps: {
            label: getTranslation({
              translationKey: 'lists.edit_list',
              fallback: 'Edit List',
            }),
            onCloseIconPress: closeModal,
          },
          modalFooterProps: {
            hasSecondaryCTA: false,
            primaryCTAText: getTranslation({
              translationKey: 'lists.save',
              fallback: 'Save',
            }),
            primaryCTAVariant: 'primary',
            primaryCTAAdditionalProps: {
              disabled: isButtonDisabled,
              processing: isLoading,
              onClick: () => onEditListButtonPress({
                description: listDescription,
                list_id: listId,
                name: listName,
              }),
            },
          },
          onRequestClose: closeModal,
        };
      }
      case MODAL_MODES.DELETE_LIST: {
        return {
          modalNavHeaderProps: {
            onCloseIconPress: closeModal,
            showDivider: false,
          },
          modalFooterProps: {
            hasSecondaryCTA: true,
            primaryCTAText: getTranslation({
              translationKey: 'lists.delete',
              fallback: 'Delete',
            }),
            primaryCTAVariant: 'double-confirm',
            primaryCTAAdditionalProps: {
              processing: isLoading,
              onClick: () => onDeleteListButtonPress(listId),
            },
            showDivider: false,
            secondaryCTAText: getTranslation({
              translationKey: 'lists.cancel',
              fallback: 'Cancel',
            }),
            secondaryCTAVariant: 'secondary',
            secondaryCTAAdditionalProps: {
              processing: isLoading,
              onClick: closeModal,
            },
          },
          onRequestClose: closeModal,
        };
      }
      case MODAL_MODES.HIT_LIST: {
        return {
          modalNavHeaderProps: {
            onCloseIconPress: closeModal,
            showDivider: false,
          },
          modalFooterProps: {
            hasSecondaryCTA: false,
            primaryCTAText: getTranslation({
              translationKey: 'lists.got_it',
              fallback: 'Got It',
            }),
            primaryCTAVariant: 'primary',
            primaryCTAAdditionalProps: {
              processing: isLoading,
              onClick: closeModal,
            },
            showDivider: false,
          },
          onRequestClose: closeModal,
        };
      }
      default: {
        return {};
      }
    }
  };

  const actionModalProps = {
    ...getResolvedActionModalProps(multiStepModalMode),
    error,
    isOpen: isMultiStepModalOpen,
    onErrorModalClose,
    styles: getModalStyles(multiStepModalMode),
  };

  return (
    <ActionModal {...actionModalProps}>
      {getModalViewFromMode(multiStepModalMode)}
    </ActionModal>
  );
};

MultiStepListModal.displayName = 'MultiStepListModal';

MultiStepListModal.defaultProps = {
  description: undefined,
  error: {
    isErrorModalOpen: false,
    message: undefined,
    title: undefined,
  },
  lists: [],
  isLoading: false,
  isMultiStepModalOpen: false,
  listId: undefined,
  urlSlug: undefined,
  multiStepModalVenueId: '',
  multiStepModalVenueContext: undefined,
  multiStepModalMode: MODAL_MODES.ADD_TO_LIST,
  name: undefined,
  newlyCreatedList: undefined,
};

MultiStepListModal.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  description: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.shape({
    isErrorModalOpen: PropTypes.bool,
    message: PropTypes.string,
    title: PropTypes.string,
  }),
  lists: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  listId: PropTypes.string,
  urlSlug: PropTypes.string,
  multiStepModalVenueId: PropTypes.number,
  multiStepModalVenueContext: PropTypes.shape({
    name: PropTypes.string,
    context: PropTypes.string,
    city: PropTypes.string,
  }),
  multiStepModalMode: PropTypes.string,
  name: PropTypes.string,
  isMultiStepModalOpen: PropTypes.bool,
  newlyCreatedList: PropTypes.shape({
    count: PropTypes.number,
    description: PropTypes.string,
    isLegacyHitlist: PropTypes.bool,
    isVenueOnList: PropTypes.bool,
    listId: PropTypes.string,
    name: PropTypes.string,
    responsiveImages: PropTypes.shape({}),
    urlSlug: PropTypes.string,
  }),
};

const mapStateToProps = ({ favorite }) => ({
  error: favorite.listsStatus.error,
  lists: favorite.lists,
  isLoading: favorite.isLoading,
  multiStepModalMode: favorite.multiStepModalMode,
  isMultiStepModalOpen: favorite.isMultiStepModalOpen,
  multiStepModalVenueId: favorite.multiStepModalVenueId,
  multiStepModalVenueContext: favorite.multiStepModalVenueContext,
  newlyCreatedList: favorite.newlyCreatedList,
});
export default connect(mapStateToProps)(MultiStepListModal);
